/**
 * Typed CRUD client for one API collection.
 *
 *     const sites = createApiResource<Site, SiteInput>('/api/v1/organization/sites/');
 *     const page  = await sites.list({ page: 1, search: 'huánuco' });
 *
 * Every maintainer screen needs the same six calls against a different path.
 * Writing them once here is the difference between a new catalogue screen
 * costing eighty lines and costing three hundred.
 */
import { apiClient } from './client';
import type { ListParams, Paginated } from './types';

export interface ApiResource<T, TInput = Partial<T>> {
  /**
   * `signal` lets TanStack Query cancel a list request whose result is already
   * obsolete — a search whose user kept typing, or a scroll that outran a
   * re-sort. Optional so every existing call site stays valid.
   */
  list: (params?: ListParams, options?: { signal?: AbortSignal }) => Promise<Paginated<T>>;
  get: (id: string) => Promise<T>;
  create: (payload: TInput) => Promise<T>;
  update: (id: string, payload: Partial<TInput> & { reason?: string }) => Promise<T>;
  remove: (id: string, reason: string) => Promise<void>;
  reactivate: (id: string, reason: string) => Promise<T>;
}

export function createApiResource<T, TInput = Partial<T>>(
  basePath: string,
): ApiResource<T, TInput> {
  const path = basePath.endsWith('/') ? basePath : `${basePath}/`;

  return {
    async list(params, options) {
      const { data } = await apiClient.get<Paginated<T>>(path, {
        params,
        // Conditional rather than `signal: options?.signal`: the project
        // compiles with `exactOptionalPropertyTypes`, where an explicit
        // `undefined` is not the same as an absent property.
        ...(options?.signal ? { signal: options.signal } : {}),
      });
      return data;
    },

    async get(id) {
      const { data } = await apiClient.get<T>(`${path}${id}/`);
      return data;
    },

    async create(payload) {
      const { data } = await apiClient.post<T>(path, payload);
      return data;
    },

    async update(id, payload) {
      const { data } = await apiClient.patch<T>(`${path}${id}/`, payload);
      return data;
    },

    /**
     * Deactivates or voids rather than destroying — the API decides which,
     * per RN-23. The reason is mandatory because RN-25 requires every
     * withdrawal to record why.
     */
    async remove(id, reason) {
      await apiClient.delete(`${path}${id}/`, { data: { reason } });
    },

    async reactivate(id, reason) {
      const { data } = await apiClient.post<T>(`${path}${id}/reactivate/`, { reason });
      return data;
    },
  };
}
